import type { LetterSummary } from '@core/letters'

/**
 * The four questions, laid out so the answers are findable without reading.
 *
 * "Wat willen ze" comes before "tegen wanneer" on purpose: a date on its own
 * is only anxiety, and the thing people actually forget is not the deadline
 * but what they were supposed to do by it.
 *
 * Where the summary could not establish something it stays blank rather than
 * guessing, and the missing-deadline warning is loud — spec.md asks for
 * exactly that, because silence is how a legal deadline gets missed.
 */
export function LetterSummaryCard({ summary }: { summary: LetterSummary }) {
  const needsDisclaimer =
    /deurwaarder|gerechtelijke|belastingdienst|overheid|sociale bijdrage/i.test(summary.what)

  return (
    <div className="space-y-3">
      <Part label="Wat is dit" value={summary.what} />
      {summary.asks && <Part label="Wat willen ze" value={summary.asks} />}

      {summary.deadline && (
        <Part
          label="Tegen wanneer"
          value={formatDate(summary.deadline)}
          hint={
            summary.deadlineSource === 'within_days'
              ? 'Geteld vanaf de dag dat de brief binnenkwam — controleer de datum op het document.'
              : undefined
          }
        />
      )}

      {summary.deadlineUnclear && (
        <div className="p-3 rounded-xl border border-warn/40 bg-warn/5">
          <p className="text-sm font-medium">Mogelijk staat hier een termijn in</p>
          <p className="text-sm text-muted">
            Er wordt iets gevraagd, maar ik kon geen datum vinden. Lees het
            origineel even zelf.
          </p>
        </div>
      )}

      {summary.ifIgnored && (
        <Part label="Als je niets doet" value={summary.ifIgnored} />
      )}

      {needsDisclaimer && (
        // Not collapsible, and not dismissible. A summary of a court letter is
        // an aid; the paper is the document.
        <p className="text-xs text-muted border-t border-line pt-3">
          Dit is een samenvatting die de app zelf maakte. Bij overheid, fiscus
          en juridische post is alleen het originele document geldig — open het
          en lees het zelf.
        </p>
      )}
    </div>
  )
}

function Part({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-xs text-muted uppercase tracking-wide">{label}</p>
      <p className="mt-0.5">{value}</p>
      {hint && <p className="text-xs text-muted mt-0.5">{hint}</p>}
    </div>
  )
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T00:00:00`))
}
